import { getSession } from "next-auth/react";

import {
  ApiClientError,
  extractApiErrorMessage,
  type ApiResponse,
  type RequestOptions,
} from "@/services/api-client";

export const ADMIN_PROXY_BASE_PATH = "/api/admin-proxy";

type AdminRequestOptions = Omit<RequestOptions, "body" | "version">;

function buildAdminUrl(
  endpoint: string,
  params?: RequestOptions["params"],
  trailingSlash?: boolean,
): URL {
  const cleanEndpoint = endpoint.replace(/^\/+/, "");
  const upstreamPath = cleanEndpoint.startsWith("api/")
    ? cleanEndpoint
    : cleanEndpoint.startsWith("v2/")
      ? `api/${cleanEndpoint}`
      : `api/v2/${cleanEndpoint}`;
  const path =
    trailingSlash && !upstreamPath.endsWith("/")
      ? `${upstreamPath}/`
      : upstreamPath;
  const origin =
    typeof window === "undefined" ? "http://localhost" : window.location.origin;
  const url = new URL(`${ADMIN_PROXY_BASE_PATH}/${path}`, origin);

  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== undefined && value !== null) {
      url.searchParams.append(key, String(value));
    }
  }
  return url;
}

async function parseResponseBody(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  try {
    return await response.json();
  } catch {
    return { message: "No response data" };
  }
}

class AdminApiClient {
  private async request<T>(
    method: string,
    endpoint: string,
    body: unknown,
    options: AdminRequestOptions,
    retriedAuth = false,
  ): Promise<ApiResponse<T>> {
    const url = buildAdminUrl(
      endpoint,
      options.params,
      options.trailingSlash,
    );
    const timeoutSignal = options.signal
      ? undefined
      : AbortSignal.timeout(options.timeoutMs ?? 15_000);
    const response = await fetch(url.toString(), {
      method,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: options.signal ?? timeoutSignal,
    });

    if (
      response.status === 401 &&
      !retriedAuth &&
      response.headers.get("x-auth-error") === "TokenExpired"
    ) {
      const refreshedSession = await getSession().catch(() => null);
      if (refreshedSession && !refreshedSession.error) {
        return this.request<T>(method, endpoint, body, options, true);
      }
    }

    const data = await parseResponseBody(response);
    if (!response.ok) {
      const record =
        data && typeof data === "object"
          ? (data as Record<string, unknown>)
          : null;
      const retryAfter = response.headers.get("retry-after")?.trim();
      throw new ApiClientError(
        extractApiErrorMessage(data) ?? "API request failed",
        response.status,
        record?.detail ?? data,
        data,
        retryAfter && /^\d+$/.test(retryAfter) ? Number(retryAfter) : null,
      );
    }

    return { data: data as T, status: response.status };
  }

  post<T>(
    endpoint: string,
    body?: unknown,
    options: AdminRequestOptions = {},
  ) {
    return this.request<T>("POST", endpoint, body, options);
  }

  getV2<T>(endpoint: string, options: AdminRequestOptions = {}) {
    return this.request<T>("GET", endpoint, undefined, options);
  }

  putV2<T>(
    endpoint: string,
    body?: unknown,
    options: AdminRequestOptions = {},
  ) {
    return this.request<T>("PUT", endpoint, body, options);
  }

  deleteV2<T>(
    endpoint: string,
    body?: unknown,
    options: AdminRequestOptions = {},
  ) {
    return this.request<T>("DELETE", endpoint, body, options);
  }
}

export const adminApiClient = new AdminApiClient();
